import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { matchPathname } from '../../../util/routes';
import { FormattedMessage } from '../../../util/reactIntl';
import { NamedLink, Logo, Icon } from '../../';
import routeConfiguration from '../../../routeConfiguration';

import LockWrapperIcon from './LockWrapperIcon';
import css from './EditLiveRoomWizardTabNav.module.css';

const Tab = props => {
  const {
    className,
    linkClassName,
    selectedLinkClassName,
    id,
    disabled,
    text,
    selected,
    linkProps,
  } = props;
  const linkClasses = classNames(css.link, linkClassName, {
    [css.selectedLink]: selected,
    [selectedLinkClassName]: selected,
    [css.disabled]: disabled,
  });

  const renderEditLiveRoomIcon = (
    <LockWrapperIcon show={disabled}>
      <Icon name="lock" size={12} />
    </LockWrapperIcon>
  );

  return (
    <div id={id} className={className}>
      <NamedLink className={linkClasses} {...linkProps}>
        {text}
        {renderEditLiveRoomIcon}
      </NamedLink>
    </div>
  );
};

Tab.defaultProps = {
  className: null,
  linkClassName: null,
  selectedLinkClassName: null,
  disabled: false,
  selected: false,
};

const { arrayOf, shape, bool, node, object, string, func } = PropTypes;

Tab.propTypes = {
  id: string.isRequired,
  className: string,
  linkClassName: string,
  selectedLinkClassName: string,
  text: node.isRequired,
  disabled: bool,
  selected: bool,
  linkProps: object.isRequired,
};

const EditLiveRoomWizardTabNav = props => {
  const {
    className,
    rootClassName,
    navWrapperRootClassName,
    tabRootClassName,
    linkClassName,
    selectedLinkClassName,
    titleClassName,
    tabs,
    tabGroups,
    title,
    location,
    isNewListingFlow,
    showLogo,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const navWrapperClasses = classNames(navWrapperRootClassName || css.navWrapper);
  const tabClasses = tabRootClassName || css.tab;
  const titleClasses = classNames(css.title, titleClassName);

  const fromPagePath = location?.state?.from || null;
  const fromPagePathWithoutSearch = fromPagePath?.indexOf('?');
  const locationFrom = fromPagePath?.includes('?')
    ? fromPagePath?.substring(0, fromPagePathWithoutSearch)
    : fromPagePath;
  const locationFromRoute = matchPathname(locationFrom, routeConfiguration())?.[0];

  // Handle default route - live rooms are creator content, so fall back to creator listings
  const locationFromRouteName = locationFromRoute?.route?.name || 'CreatorManageListingsPage';

  // If locationFromRoute exists and contains params, use them, otherwise use an empty object.
  const locationFromRouteParamsMaybe = locationFromRoute?.params
    ? { params: { ...locationFromRoute.params } }
    : {};

  const returnLink = (
    <NamedLink
      className={css.saveAndExitLink}
      name={locationFromRouteName}
      {...locationFromRouteParamsMaybe}
    >
      {isNewListingFlow ? (
        <FormattedMessage id="EditLiveRoomWizard.exit" />
      ) : (
        <FormattedMessage id="EditLiveRoomWizard.saveAndExit" />
      )}
    </NamedLink>
  );

  return (
    <nav className={classes}>
      <div className={navWrapperClasses}>
        {showLogo ? (
          <div className={css.logoWrapper}>
            <span className={css.logoContainer}>
              <Logo className={css.logo} format="mobile" />
            </span>
            {returnLink}
          </div>
        ) : null}
        {title ? <h2 className={titleClasses}>{title}</h2> : null}
        <div className={classNames(css.arrowContainer, css.tab)}>
          <NamedLink name="CreatorManageListingsPage" className={css.link}>
            <Icon name="chevronLeft" />
          </NamedLink>
        </div>
        {tabGroups
          ? tabGroups.map(group => (
              <div className={css.tabGroup} key={group.label}>
                <span className={css.tabGroupLabel}>{group.label}</span>
                {group.tabs.map((tab, index) => {
                  const id = typeof tab.id === 'string' ? tab.id : `${index}`;
                  return (
                    <Tab
                      key={id}
                      id={id}
                      className={tabClasses}
                      linkClassName={linkClassName}
                      selectedLinkClassName={selectedLinkClassName}
                      {...tab}
                    />
                  );
                })}
              </div>
            ))
          : tabs.map((tab, index) => {
              const id = typeof tab.id === 'string' ? tab.id : `${index}`;
              return (
                <Tab
                  key={id}
                  id={id}
                  className={tabClasses}
                  linkClassName={linkClassName}
                  selectedLinkClassName={selectedLinkClassName}
                  {...tab}
                />
              );
            })}
      </div>
    </nav>
  );
};

EditLiveRoomWizardTabNav.defaultProps = {
  className: null,
  rootClassName: null,
  navWrapperRootClassName: null,
  tabRootClassName: null,
  tabClassName: null,
  linkClassName: null,
  selectedLinkClassName: null,
  titleClassName: null,
  title: null,
  isNewListingFlow: false,
  showLogo: false,
};

EditLiveRoomWizardTabNav.propTypes = {
  className: string,
  rootClassName: string,
  navWrapperRootClassName: string,
  tabRootClassName: string,
  linkClassName: string,
  selectedLinkClassName: string,
  titleClassName: string,
  title: string,
  isNewListingFlow: bool.isRequired,
  showLogo: bool,
  tabs: arrayOf(object).isRequired,

  // from withRouter
  location: shape({
    search: string.isRequired,
  }).isRequired,
};

export default compose(withRouter)(EditLiveRoomWizardTabNav);
